import { TooltipProvider } from "@radix-ui/react-tooltip";
import type {
  ProfileMetaData,
  SkillTag,
  UserProfile,
} from "../../../../../client";
import {
  Image,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../../components";
import { cn } from "../../../../../components/utils/cn";
import { getImageUrl } from "../../../../../utils/image";
import { iconForName } from "../../../../../components/TechIcons";
import SkillsIcon from "../../../../../components/atoms/SkillsIcon";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareCheck } from "@fortawesome/free-solid-svg-icons";
import { useStatsigClient } from "../../../../../hooks";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";

interface Props extends UserProfile {
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  matchingSkills?: SkillTag[];
  availableDate?: string | null;
  haveContract: boolean;
  industryMatch?: ProfileMetaData | null;
}

export const MemberItem = ({
  profileData,
  profilePhotoId,
  selected,
  onClick,
  disabled,
  matchingSkills,
  availableDate,
  haveContract,
  industryMatch,
}: Props) => {
  const statsigClient = useStatsigClient();
  const isDateMoreThan6MonthsAway = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = date.getTime() - now.getTime();
      const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
      return diffInDays > 180; // 6 months ≈ 180 days
    } catch {
      return false;
    }
  };

  const renderAvailableDate = (dateString: string) => {
    const isMoreThan6Months = isDateMoreThan6MonthsAway(dateString);

    if (isMoreThan6Months) {
      return null;
    }

    const formattedDate = formatAvailableDate(dateString);

    return (
      <div className="flex flex-row rounded-3xl bg-red-1 text-red-3 text-[10px] items-center justify-center gap-1 p-2 font-semibold">
        <FontAwesomeIcon icon={faCircleCheck} className="text-red-2" />
        {formattedDate}
      </div>
    );
  };

  const formatAvailableDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = date.getTime() - now.getTime();
      const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays <= 7) {
        if (diffInDays === 1) return "1 day left";
        return `available in ${diffInDays} days`;
      } else if (diffInDays <= 30) {
        const weeks = Math.round(diffInDays / 7);
        if (weeks === 1) return "available in 1 week";
        return `available in ${weeks} weeks`;
      } else if (diffInDays <= 365) {
        const months = Math.round(diffInDays / 30);
        if (months === 1) return "available in 1 month";
        return `available in ${months} months`;
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    } catch {
      return dateString;
    }
  };

  return (
    <button
      type="button"
      className={cn(
        "flex flex-col p-4 hover:bg-gray-2 rounded-2xl border border-transparent transition-all active:scale-95 disabled:opacity-15",
        selected && "bg-gray-3 border-gray-4",
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex flex-row gap-2 items-center max-w-fit">
        <Image
          src={profilePhotoId ? getImageUrl(profilePhotoId) : undefined}
          alt={
            typeof profileData.name === "string" ? profileData.name : "Profile"
          }
          className="rounded-xl w-10 h-10 flex-shrink-0"
        />
        <div>
          <p className="text-sm truncate text-left">
            {typeof profileData.name === "string"
              ? profileData.name
              : "Unknown"}
          </p>

          {matchingSkills && (
            <div className="flex gap-1">
              {matchingSkills.map((skill, index) => (
                <TooltipProvider
                  key={`${skill.name}-${index}`}
                  delayDuration={0}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <SkillsIcon profileSkillTag={skill} text={skill.name} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="mb-2 z-10 bg-white">
                      <p>{skill.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          )}
        </div>
      </div>
      {statsigClient?.checkGate("view_matching_tags") && (
        <div className="flex flex-row gap-2 items-center justify-start mt-2">
          {matchingSkills && matchingSkills.length > 0 && (
            <div className="flex flex-row rounded-3xl bg-red-1 text-red-3 text-[10px] items-center justify-center gap-1 p-2 font-semibold">
              <FontAwesomeIcon icon={faCircleCheck} className="text-red-2" />
              {matchingSkills?.length} skills
            </div>
          )}

          {availableDate != null ? (
            renderAvailableDate(availableDate)
          ) : !haveContract ? (
            <div className="flex flex-row rounded-3xl bg-red-1 text-red-3 text-[10px] items-center justify-center gap-1 p-2 font-semibold">
              <FontAwesomeIcon icon={faCircleCheck} className="text-red-2" />
              available now!
            </div>
          ) : null}

          {industryMatch && (
            <TooltipProvider
              key={`${industryMatch.valueTag}-${industryMatch.id}`}
              delayDuration={0}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-row rounded-3xl bg-red-1 text-red-3 text-[10px] items-center justify-center gap-1 p-2 font-semibold">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-red-2"
                    />
                    Industry match!
                  </div>
                </TooltipTrigger>
                <TooltipContent className="mb-2 z-10 bg-white">
                  <p>{industryMatch.valueTag}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </button>
  );
};

MemberItem.displayName = "MemberItem";
